import React from "react";
import { FolderKanban } from "lucide-react";
import StyledSelect from "../ui/StyledSelect";
import { cn } from "../../lib/utils";

const PERIODS = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
];

export const ALL_PROJECTS = "";

/**
 * Scope controls for the Analytics dashboard: which project, and over what window.
 *
 * Both live in the URL (see AIDashboard), so a filtered dashboard is a link you can
 * send to someone. "All projects" is the empty value — StyledSelect supports an
 * empty-string option, which is why it is used here instead of a sentinel string
 * that would then have to be translated back to "no project_id" at the call site.
 */
export function ScopeBar({
  projects = [],
  projectId = ALL_PROJECTS,
  onProjectChange,
  period = "30d",
  onPeriodChange,
  loading = false,
  disabled = false,
  showPeriod = true,
  // Las vistas que no pueden responder por toda la cuenta no deben ofrecerlo:
  // una opcion que no funciona es peor que no tenerla.
  allowAll = true,
  // En móvil hay dos sitios donde vive esta barra: en su propia línea —donde
  // cabe entera— o compartiendo fila con el título de la sección, donde no.
  // `compact` es para el segundo: se cae el icono y el ancho mínimo, porque el
  // nombre del proyecto ya dice qué es y los 176px se los estaría quitando al
  // título. En escritorio los dos modos se ven igual.
  compact = false,
}) {
  const projectOptions = [
    ...(allowAll ? [{ value: ALL_PROJECTS, label: "All projects" }] : []),
    ...(Array.isArray(projects) ? projects : [])
      .filter((p) => p?.id)
      .map((p) => ({ value: p.id, label: p.name || "Untitled project" })),
  ];

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <div className="flex min-w-0 items-center gap-2" title="Filter metrics by project">
        <FolderKanban
          className={cn(
            "h-4 w-4 flex-shrink-0 text-gray-400",
            compact && "hidden md:block"
          )}
          aria-hidden
        />
        <StyledSelect
          value={projectId}
          onChange={onProjectChange}
          options={projectOptions}
          disabled={disabled || loading}
          placeholder={loading ? "Loading projects…" : "All projects"}
          className={cn("py-2", compact ? "min-w-0 md:min-w-[11rem]" : "min-w-[11rem]")}
        />
      </div>

      {showPeriod && (
      <div
        role="group"
        aria-label="Period"
        className="inline-flex items-center gap-1 rounded-xl border border-white/10 bg-[#0a0a0a]/70 p-1 backdrop-blur-sm"
      >
        {PERIODS.map((option) => {
          const isActive = option.key === period;
          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={isActive}
              disabled={disabled}
              onClick={() => onPeriodChange?.(option.key)}
              className={cn(
                "rounded-lg px-3 py-1.5 text-xs font-medium tabular-nums transition-colors",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60",
                "disabled:cursor-not-allowed disabled:opacity-50",
                isActive
                  ? "bg-violet-500/20 text-violet-100 ring-1 ring-violet-400/30"
                  : "text-gray-400 hover:bg-white/5 hover:text-gray-200"
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      )}
    </div>
  );
}

export default ScopeBar;
